import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Lock, Crown, ShieldCheck, Activity } from "lucide-react";
import { api } from "../lib/api";

function scoreColor(score) {
  if (score >= 80) return "var(--success-green, #16a34a)";
  if (score >= 60) return "#0057ff";
  if (score >= 40) return "#d97706";
  return "var(--alert-red, #FF2B00)";
}

function stressColor(pct) {
  if (pct >= 70) return "var(--alert-red, #FF2B00)";
  if (pct >= 45) return "#d97706";
  return "var(--success-green, #16a34a)";
}

export default function WellnessPaywallPreview({ month }) {
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    let ignore = false;
    api
      .get("/wellness/preview", { params: month ? { month } : {} })
      .then((r) => !ignore && setPreview(r.data))
      .catch(() => !ignore && setPreview({ score: 72, stress: 48, month: "" })); // fallback demo
    return () => {
      ignore = true;
    };
  }, [month]);

  const score = preview?.score ?? 0;
  const stress = preview?.stress ?? 0;
  const circumference = 2 * Math.PI * 54;
  const dash = (score / 100) * circumference;

  return (
    <div className="relative" data-testid="wellness-paywall-preview">
      {/* Blurred real preview */}
      <div
        aria-hidden
        className="grid grid-cols-1 lg:grid-cols-2 gap-4 select-none pointer-events-none"
        style={{ filter: "blur(6px)", WebkitFilter: "blur(6px)" }}
      >
        <div className="bg-white border-2 border-black p-6">
          <div className="overline flex items-center gap-2 mb-3">
            <ShieldCheck size={12} /> FINANCIAL WELLNESS SCORE
          </div>
          <div className="flex items-center gap-6">
            <div className="relative shrink-0" style={{ width: 128, height: 128 }}>
              <svg width="128" height="128" viewBox="0 0 128 128">
                <circle cx="64" cy="64" r="54" fill="none" stroke="#0000000f" strokeWidth="12" />
                <circle
                  cx="64" cy="64" r="54" fill="none"
                  stroke={scoreColor(score)} strokeWidth="12"
                  strokeDasharray={`${dash} ${circumference}`}
                  transform="rotate(-90 64 64)"
                />
              </svg>
              <div className="absolute inset-0 flex flex-col items-center justify-center">
                <div className="font-display font-black text-4xl tracking-tighter" style={{ color: scoreColor(score) }}>
                  {Math.round(score)}
                </div>
                <div className="font-mono text-[10px] opacity-60 mt-1">/ 100</div>
              </div>
            </div>
            <div className="flex-1">
              <div className="font-display font-bold text-lg" style={{ color: scoreColor(score) }}>
                HEALTHY
              </div>
              <p className="font-mono text-xs opacity-60 mt-1">
                Composite of budget, goals and savings.
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-3 mt-6">
            <div>
              <div className="overline mb-1">BUDGET / 40%</div>
              <div className="h-2 bg-black/10"><div className="h-full" style={{ width: "82%", background: "#16a34a" }} /></div>
            </div>
            <div>
              <div className="overline mb-1">GOALS / 35%</div>
              <div className="h-2 bg-black/10"><div className="h-full" style={{ width: "68%", background: "#0057ff" }} /></div>
            </div>
            <div>
              <div className="overline mb-1">SAVINGS / 25%</div>
              <div className="h-2 bg-black/10"><div className="h-full" style={{ width: "44%", background: "#d97706" }} /></div>
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-black p-6">
          <div className="overline flex items-center gap-2 mb-3">
            <Activity size={12} /> FINANCIAL STRESS INDICATOR
          </div>
          <div className="flex items-baseline gap-3">
            <span className="font-mono text-sm opacity-70">Financial Stress</span>
            <span className="font-display font-black text-4xl tracking-tighter" style={{ color: stressColor(stress) }}>
              {Math.round(stress)}%
            </span>
          </div>
          <div className="mt-5 space-y-2">
            {["Spending exceeds budget", "Savings rate", "Emergency fund", "Bill payment", "Goal progress", "Volatility", "Discretionary"].map((label, i) => (
              <div key={label} className="flex items-center gap-3">
                <div className="w-40 shrink-0 font-mono text-xs opacity-70">{label}</div>
                <div className="flex-1 h-1.5 bg-black/10">
                  <div className="h-full" style={{ width: `${20 + i * 10}%`, background: "#FF2B00" }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Paywall overlay */}
      <div
        className="absolute inset-0 flex items-center justify-center p-6"
        data-testid="wellness-paywall-overlay"
      >
        <div className="bg-[#111111] text-white p-8 border-2 border-black text-center max-w-lg shadow-[8px_8px_0_0_rgba(0,0,0,0.15)]">
          <div className="flex items-center justify-center">
            <div className="w-14 h-14 bg-[var(--alert-red)] flex items-center justify-center">
              <Lock size={26} />
            </div>
          </div>
          <div className="overline text-white/60 mt-4 flex items-center justify-center gap-2">
            <Crown size={12} /> PREMIUM · SPECIAL LAUNCH PRICE
          </div>
          <h3 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-2">
            Your Wellness Score is <span className="text-[var(--alert-red)]">{Math.round(score)}</span>
          </h3>
          <p className="font-mono text-xs text-white/70 mt-3 leading-relaxed">
            Unlock the full breakdown — Budget, Goals & Savings sub-scores, a 7-factor Stress Indicator with monthly trend, the 6-month trajectory chart, and a live AI note explaining exactly why your score moved.
          </p>
          <div className="mt-5 inline-flex items-center gap-3 border-2 border-[var(--alert-red)] px-3 py-1.5">
            <span className="font-mono text-sm text-white/50 line-through">₹499</span>
            <span className="font-display font-black text-xl tracking-tighter">₹200</span>
            <span className="bg-[var(--alert-red)] text-white font-display font-black text-[10px] px-2 py-0.5 tracking-widest">
              SAVE ₹299
            </span>
          </div>
          <Link
            to="/upgrade"
            data-testid="paywall-upgrade-link"
            className="mt-4 inline-block bg-[var(--alert-red)] text-white px-8 py-3 font-display font-bold tracking-wider hover:bg-white hover:text-black transition-colors"
          >
            UNLOCK FOR ₹200 →
          </Link>
          <div className="mt-3 font-mono text-[10px] text-white/40">
            One-time payment. Launch price. Lifetime access.
          </div>
        </div>
      </div>
    </div>
  );
}
